const STORAGE_KEY = 'lithoform-stars';
const TIME_STORAGE_KEY = 'lithoform-time-stars';
const BASE_POINTS = 10;

export interface LevelStars {
  [level: number]: { stars: number; bestScore: number };
}

export interface LevelTimeStars {
  [level: number]: { stars: number; bestTime: number };
}

export function getParScore(level: number): number {
  const gridSize = 7;
  const poolIndex = (level - 1) % 5;
  const repeat = Math.floor((level - 1) / 5);
  const complexityMultiplier = 1 + poolIndex * 0.1;
  const progressionMultiplier = 1 + repeat * 0.2;
  return Math.floor(gridSize * gridSize * BASE_POINTS * complexityMultiplier * progressionMultiplier);
}

export function calculateStars(score: number, level: number): number {
  if (score <= 0) return 0;
  const par = getParScore(level);
  if (score >= par * 2.5) return 3;
  if (score >= par * 1.5) return 2;
  return 1;
}

export function getLevelStars(level: number): { stars: number; bestScore: number } | null {
  const all = loadAllStars();
  return all[level] ?? null;
}

export function saveLevelStars(level: number, score: number): void {
  const all = loadAllStars();
  const stars = calculateStars(score, level);
  const existing = all[level];

  if (!existing || stars > existing.stars || score > existing.bestScore) {
    all[level] = {
      stars: Math.max(stars, existing?.stars ?? 0),
      bestScore: Math.max(score, existing?.bestScore ?? 0),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  }
}

function loadAllStars(): LevelStars {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as LevelStars;
  } catch {
    return {};
  }
}

export function getParTime(level: number): number {
  const gridCells = 7 * 7;
  const repeat = Math.floor((level - 1) / 5);
  const progressionMultiplier = 1 + repeat * 0.2;
  return gridCells * 3 * progressionMultiplier;
}

export function calculateTimeStars(elapsedSeconds: number, level: number): number {
  const par = getParTime(level);
  if (elapsedSeconds <= par * 0.5) return 3;
  if (elapsedSeconds <= par) return 2;
  return 1;
}

export function getLevelTimeStars(level: number): { stars: number; bestTime: number } | null {
  const all = loadAllTimeStars();
  return all[level] ?? null;
}

export function saveLevelTimeStars(level: number, elapsedMs: number): void {
  const all = loadAllTimeStars();
  const elapsedSeconds = elapsedMs / 1000;
  const stars = calculateTimeStars(elapsedSeconds, level);
  const existing = all[level];

  if (!existing || stars > existing.stars || elapsedMs < existing.bestTime) {
    all[level] = {
      stars: Math.max(stars, existing?.stars ?? 0),
      bestTime: Math.min(elapsedMs, existing?.bestTime ?? Infinity),
    };
    localStorage.setItem(TIME_STORAGE_KEY, JSON.stringify(all));
  }
}

function loadAllTimeStars(): LevelTimeStars {
  const raw = localStorage.getItem(TIME_STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as LevelTimeStars;
  } catch {
    return {};
  }
}
